import { FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { DndContext, useDraggable, DragEndEvent } from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import { css } from '@emotion/react';
import { debounce } from 'lodash-es';
import { useIsSmallMode } from '@frontend/shared';
import { usePortal } from '@frontend/design-system';
import { CHATBOT_DRAGGABLE_ID } from '../../constant';
import { useChatbotAssistantConfig } from '../../hooks/use-chatbot-assistant-config';
import { ChatbotAssistantContext, useChatbotAssistant } from '../../provider/chatbot-assistant.provider';
import { WeaveCopilotSource } from '../../types';
import { calculateDefaultPosition } from '../../utils/position';
import { TriggerButton } from '../trigger-button';
import { ResizableChatbotWidget } from './chatbot-widget';

export const ChatbotAssistant: FC<{ source?: WeaveCopilotSource }> = ({ source = 'top-bar' }) => {
  const chatbotAssistantConfig = useChatbotAssistantConfig({ source });
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const { position, setPosition } = chatbotAssistantConfig;
  const { isSmallMode } = useIsSmallMode();

  useEffect(() => {
    if (position === null && triggerButtonRef.current) {
      setPosition(calculateDefaultPosition(triggerButtonRef.current));
    }
  }, [position, setPosition]);

  useEffect(() => {
    const handleResize = debounce(() => {
      !!triggerButtonRef.current && setPosition(calculateDefaultPosition(triggerButtonRef.current));
    }, 100);

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setPosition]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { delta } = event;

      setPosition({
        x: (position?.x ?? 0) + delta.x,
        y: (position?.y ?? 0) + delta.y,
      });
    },
    [setPosition, position]
  );

  if (isSmallMode) {
    return null;
  }

  return (
    <ChatbotAssistantContext.Provider value={chatbotAssistantConfig}>
      <TriggerButton innerRef={triggerButtonRef} />
      <DndContext onDragEnd={handleDragEnd} modifiers={[restrictToWindowEdges]}>
        <DraggableChatbotWidget />
      </DndContext>
    </ChatbotAssistantContext.Provider>
  );
};

const DraggableChatbotWidget: FC = () => {
  const { position, isOpen, isPopoutOpen } = useChatbotAssistant();

  const { Portal } = usePortal({
    attributes: { id: CHATBOT_DRAGGABLE_ID },
  });

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: CHATBOT_DRAGGABLE_ID,
  });

  const dragHandleProps = useMemo(() => ({ attributes, listeners }), [attributes, listeners]);

  return (
    <Portal>
      {isOpen && !isPopoutOpen && (
        <div
          ref={setNodeRef}
          css={[
            styles.draggableBox({
              x: (position?.x ?? 0) + (transform?.x ?? 0),
              y: (position?.y ?? 0) + (transform?.y ?? 0),
              isDragging,
            }),
          ]}
        >
          <ResizableChatbotWidget isDraggable dragHandleProps={dragHandleProps} />
        </div>
      )}
    </Portal>
  );
};

const styles = {
  draggableBox: ({ x, y, isDragging }: { x: number; y: number; isDragging: boolean }) => css`
    position: fixed;
    left: ${x}px;
    top: ${y}px;
    transition: transform 0.2s ease;
    ${isDragging && 'transform: scale(1.05);'}
  `,
  dragHandle: css`
    cursor: grab;
    &:active {
      cursor: grabbing;
    }
  `,
};
